import React from 'react';
import { View, StyleSheet, Platform } from 'react-native';
import { Home, MapPin, Layers } from 'lucide-react-native';
import { useTheme, BORDER_RADIUS, ELEVATION } from '../../theme';
import { Structure } from '../../types';
import { getReadinessLevel } from '../../utils/readiness';

interface StructureMarkerProps {
  structure: Structure;
  isSelected?: boolean;
  size?: 'small' | 'medium' | 'large';
}

// This component renders the marker content
// For native, it should be wrapped in a MapView Marker
// For web, it can be used in a Leaflet custom icon
export function StructureMarker({
  structure,
  isSelected = false,
  size = 'medium',
}: StructureMarkerProps) {
  const { colors } = useTheme();

  const isClaimed = Boolean(structure.claimSummary?.claimedBy);
  const readiness = getReadinessLevel(structure);

  // Readiness color wins over the claimed/available fallback so claimed
  // structures with progress data show red/amber/green. Selection still
  // overrides everything for map focus.
  const readinessColor =
    readiness === 'progress'
      ? colors.progress
      : readiness === 'partial'
        ? colors.partial
        : readiness === 'risk'
          ? colors.risk
          : null;

  const markerColor = isSelected
    ? colors.mapMarkerSelected
    : (readinessColor ?? (isClaimed ? colors.mapMarkerClaimed : colors.mapMarkerAvailable));

  const sizes = {
    small: { container: 24, icon: 12 },
    medium: { container: 32, icon: 16 },
    large: { container: 40, icon: 20 },
  };

  const { container: containerSize, icon: iconSize } = sizes[size];

  return (
    <View
      style={[
        styles.container,
        {
          width: containerSize,
          height: containerSize,
          backgroundColor: markerColor,
          borderColor: colors.white,
        },
        isSelected && styles.selected,
        Platform.OS !== 'web' && ELEVATION.sm,
      ]}>
      {isClaimed ? (
        <Home size={iconSize} color={colors.white} />
      ) : (
        <MapPin size={iconSize} color={colors.white} />
      )}
      {/* Pointer triangle */}
      <View
        style={[
          styles.pointer,
          {
            borderTopColor: markerColor,
          },
        ]}
      />
    </View>
  );
}

// Simplified marker for map clustering
export function StructureClusterMarker({
  count: _count,
  size = 'medium',
}: {
  count: number;
  size?: 'small' | 'medium' | 'large';
}) {
  const { colors } = useTheme();

  const sizes = {
    small: { container: 32, fontSize: 11 },
    medium: { container: 40, fontSize: 13 },
    large: { container: 48, fontSize: 15 },
  };

  const { container: containerSize, fontSize } = sizes[size];

  return (
    <View
      style={[
        styles.clusterContainer,
        {
          width: containerSize,
          height: containerSize,
          backgroundColor: colors.primary,
          borderColor: colors.white,
        },
        Platform.OS !== 'web' && ELEVATION.sm,
      ]}>
      <Layers size={fontSize} color={colors.white} style={{ opacity: 0.7 }} />
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    borderRadius: BORDER_RADIUS.full,
    borderWidth: 2,
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
  },
  selected: {
    transform: [{ scale: 1.2 }],
  },
  pointer: {
    position: 'absolute',
    bottom: -8,
    width: 0,
    height: 0,
    borderLeftWidth: 6,
    borderRightWidth: 6,
    borderTopWidth: 8,
    borderLeftColor: 'transparent',
    borderRightColor: 'transparent',
  },
  clusterContainer: {
    borderRadius: BORDER_RADIUS.full,
    borderWidth: 2,
    alignItems: 'center',
    justifyContent: 'center',
  },
});
